import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { clientRequestHeaders } from "@/lib/sm-api";
import { AdminGlassPanel, adminStatTileClass } from "./AdminGlassPanel";
import { BUSINESS_PROFILE_LABELS } from "@/lib/business-profile";

interface InfraRow {
  provider: string;
  metric: string;
  total: number;
}

interface PlatformUsageData {
  totals: Array<{ metric: string; total: number }>;
  mrr?: {
    cents: number;
    ars: number;
    packRevenueCents: number;
    subscriptionRevenueCents: number;
    arrCents?: number;
    arrArs?: number;
  };
  economics?: {
    estimatedInfraCostUsd: number;
    estimatedMarginPercent: number | null;
    arpuArs: number | null;
  };
  churn?: {
    cancelledThisPeriod: number;
    churnRatePercent: number | null;
    note: string;
  };
  trials?: {
    active: number;
    convertedThisPeriod: number;
    conversionRatePercent: number | null;
  };
  planDistribution?: Array<{ planId: string; count: number }>;
  niches?: Array<{ businessProfile: string; count: number }>;
  infra?: InfraRow[];
  topConsumers: {
    email: Array<{ tenantId: string; name: string; total: number }>;
    orders: Array<{ tenantId: string; name: string; total: number }>;
  };
  tenantUsage?: {
    note: string;
    rows: Array<{
      tenantId: string;
      name: string;
      planId: string;
      provider: string;
      byo: boolean;
      emailUsed: number;
      estimatedCost: string;
    }>;
  };
  subscriptions: { active: number; total: number };
}

export function PlatformUsageOpsPanel() {
  const [data, setData] = useState<PlatformUsageData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void (async () => {
      try {
        const r = await fetch("/api/admin/platform-usage", {
          credentials: "include",
          headers: clientRequestHeaders(),
        });
        if (r.ok) setData((await r.json()) as PlatformUsageData);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="glass-card flex items-center justify-center gap-2 rounded-xl p-8 text-sm text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" /> Cargando métricas de uso…
      </div>
    );
  }
  if (!data) return <p className="text-sm text-muted-foreground">Sin datos de usage (¿migración 018 aplicada?).</p>;

  return (
    <AdminGlassPanel
      title="Uso y economía"
      description="MRR, churn, conversión de trials y consumo por tenant — demos/seed excluidos de todos los KPIs."
    >
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div className={adminStatTileClass}>
          <p className="text-xs text-muted-foreground">Suscripciones activas</p>
          <p className="mt-1 text-2xl font-semibold tabular-nums">{data.subscriptions.active}</p>
        </div>
        <div className={adminStatTileClass}>
          <p className="text-xs text-muted-foreground">MRR estimado</p>
          <p className="mt-1 text-2xl font-semibold tabular-nums">${(data.mrr?.ars ?? 0).toLocaleString("es-AR")}</p>
        </div>
        <div className={adminStatTileClass}>
          <p className="text-xs text-muted-foreground">ARR (MRR × 12)</p>
          <p className="mt-1 text-2xl font-semibold tabular-nums">
            ${(data.mrr?.arrArs ?? (data.mrr?.ars ?? 0) * 12).toLocaleString("es-AR")}
          </p>
        </div>
        <div className={adminStatTileClass}>
          <p className="text-xs text-muted-foreground">ARPU (pagos, mes)</p>
          <p className="mt-1 text-2xl font-semibold tabular-nums">
            {data.economics?.arpuArs != null ? `$${data.economics.arpuArs.toLocaleString("es-AR")}` : "—"}
          </p>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div className={adminStatTileClass}>
          <p className="text-xs text-muted-foreground">Margen bruto est.</p>
          <p className="mt-1 text-2xl font-semibold tabular-nums">
            {data.economics?.estimatedMarginPercent != null ? `${data.economics.estimatedMarginPercent}%` : "—"}
          </p>
        </div>
        <div className={adminStatTileClass} title={data.churn?.note}>
          <p className="text-xs text-muted-foreground">Churn (mes, aprox.)</p>
          <p className="mt-1 text-2xl font-semibold tabular-nums">
            {data.churn?.churnRatePercent != null ? `${data.churn.churnRatePercent}%` : "—"}
          </p>
          <p className="text-[10px] text-muted-foreground">{data.churn?.cancelledThisPeriod ?? 0} canceladas</p>
        </div>
        <div className={adminStatTileClass}>
          <p className="text-xs text-muted-foreground">Trials activos</p>
          <p className="mt-1 text-2xl font-semibold tabular-nums">{data.trials?.active ?? 0}</p>
        </div>
        <div className={adminStatTileClass}>
          <p className="text-xs text-muted-foreground">Conversión trial→pago (mes)</p>
          <p className="mt-1 text-2xl font-semibold tabular-nums">
            {data.trials?.conversionRatePercent != null ? `${data.trials.conversionRatePercent}%` : "—"}
          </p>
          <p className="text-[10px] text-muted-foreground">{data.trials?.convertedThisPeriod ?? 0} convertidos</p>
        </div>
      </div>

      {data.mrr ? (
        <div className="grid gap-3 sm:grid-cols-2 text-sm">
          <div className={adminStatTileClass}>
            <p className="text-xs text-muted-foreground">Suscripciones (mes)</p>
            <p className="font-medium">${Math.round(data.mrr.subscriptionRevenueCents / 100).toLocaleString("es-AR")}</p>
          </div>
          <div className={adminStatTileClass}>
            <p className="text-xs text-muted-foreground">Packs (mes)</p>
            <p className="font-medium">${Math.round(data.mrr.packRevenueCents / 100).toLocaleString("es-AR")}</p>
          </div>
        </div>
      ) : null}

      <div className="grid gap-4 md:grid-cols-2">
        {data.planDistribution && data.planDistribution.length > 0 ? (
          <div>
            <h4 className="mb-2 text-sm font-medium">Distribución por plan</h4>
            <ul className="space-y-1 text-sm">
              {data.planDistribution.map((p) => (
                <li key={p.planId} className="flex justify-between border-b py-1">
                  <span>{p.planId}</span>
                  <span className="text-muted-foreground">{p.count}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        {data.niches && data.niches.length > 0 ? (
          <div>
            <h4 className="mb-2 text-sm font-medium">Por nicho/vertical</h4>
            <ul className="space-y-1 text-sm">
              {data.niches.map((n) => (
                <li key={n.businessProfile} className="flex justify-between border-b py-1">
                  <span>{BUSINESS_PROFILE_LABELS[n.businessProfile as keyof typeof BUSINESS_PROFILE_LABELS] ?? n.businessProfile}</span>
                  <span className="text-muted-foreground">{n.count}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>

      {data.infra && data.infra.length > 0 ? (
        <div>
          <h4 className="text-sm font-medium mb-2">Infra metering (mes)</h4>
          <ul className="text-sm space-y-1 max-h-48 overflow-y-auto">
            {data.infra.map((row) => (
              <li key={`${row.provider}-${row.metric}`} className="flex justify-between border-b py-1">
                <span className="font-mono text-xs">{row.provider}.{row.metric}</span>
                <span className="text-muted-foreground">{Math.round(row.total).toLocaleString("es-AR")}</span>
              </li>
            ))}
          </ul>
          {data.economics ? (
            <p className="text-xs text-muted-foreground mt-2">
              Costo infra estimado: ~USD {data.economics.estimatedInfraCostUsd.toFixed(2)} / mes.
            </p>
          ) : null}
        </div>
      ) : null}

      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <h4 className="text-sm font-medium mb-2">Top email (mes)</h4>
          <ul className="text-sm space-y-1">
            {data.topConsumers.email.map((t) => (
              <li key={t.tenantId} className="flex justify-between border-b py-1">
                <span>{t.name}</span>
                <span className="text-muted-foreground">{t.total}</span>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h4 className="text-sm font-medium mb-2">Top pedidos (mes)</h4>
          <ul className="text-sm space-y-1">
            {data.topConsumers.orders.map((t) => (
              <li key={t.tenantId} className="flex justify-between border-b py-1">
                <span>{t.name}</span>
                <span className="text-muted-foreground">{t.total}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {data.tenantUsage?.rows?.length ? (
        <div className="mt-6">
          <h4 className="text-sm font-medium mb-2">Tenant usage (email)</h4>
          <p className="mb-2 text-xs text-muted-foreground">{data.tenantUsage.note}</p>
          <div className="max-h-72 overflow-auto rounded-lg border">
            <table className="w-full text-xs">
              <thead className="bg-muted/50 text-left">
                <tr>
                  <th className="px-2 py-1.5">Tenant</th>
                  <th className="px-2 py-1.5">Plan</th>
                  <th className="px-2 py-1.5">Provider</th>
                  <th className="px-2 py-1.5">BYO</th>
                  <th className="px-2 py-1.5 text-right">Emails mes</th>
                  <th className="px-2 py-1.5">Costo</th>
                </tr>
              </thead>
              <tbody>
                {data.tenantUsage.rows.map((row) => (
                  <tr key={row.tenantId} className="border-t">
                    <td className="px-2 py-1.5">{row.name}</td>
                    <td className="px-2 py-1.5">{row.planId}</td>
                    <td className="px-2 py-1.5">{row.provider}</td>
                    <td className="px-2 py-1.5">{row.byo ? "sí" : "no"}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{row.emailUsed}</td>
                    <td className="px-2 py-1.5 text-muted-foreground">{row.estimatedCost}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </AdminGlassPanel>
  );
}
